import React, { useEffect, useState } from "react";
import { Button, CloseButton, Modal, Row, Col, Alert } from "react-bootstrap";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";

interface IProps {
  closeFilterModal: () => void;
  selectedFilter: any;
  setSelectedFilter: any;
  handleSubmit: (selectedFilterValues: any) => void;
}

const InboxFilterModal: React.FC<IProps> = ({
  closeFilterModal,
  selectedFilter,
  setSelectedFilter,
  handleSubmit,
}) => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [dateTo, setDateTo] = useState<string>("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [assigneeSuggestions, setAssigneeSuggestions] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null);

  const filters = [
    { label: "Customer email", value: "action__contact__email__icontains" },
    { label: "Agent name", value: "action__assignee__username__icontains" },
    { label: "Subject", value: "original_subject__icontains" },
    { label: "Date", value: "created" },
  ];

  useEffect(() => {
    const savedFilters = sessionStorage.getItem("selectedFilters");
    const savedFilterValues = sessionStorage.getItem("selectedFilter");
    if (savedFilters && savedFilterValues) {
      setSelectedFilters(
        savedFilters && savedFilters !== "undefined" ? JSON.parse(savedFilters) : []
      );
      setSelectedFilter(
        savedFilterValues && savedFilterValues !== "undefined"
          ? JSON.parse(savedFilterValues)
          : {}
      );
      setDateTo(
        savedFilterValues && savedFilterValues !== "undefined"
          ? JSON.parse(savedFilterValues).created || ""
          : ""
      );
    } else {
      const appliedFilters = Object.keys(selectedFilter).filter(
        (key) => selectedFilter[key]
      );
      setSelectedFilters(appliedFilters);
      setDateTo(selectedFilter.created || "");
    }
  }, [setSelectedFilter]);

  useEffect(() => {
    sessionStorage.setItem("selectedFilters", JSON.stringify(selectedFilters));
    sessionStorage.setItem("selectedFilter", JSON.stringify(selectedFilter));
  }, [selectedFilters, selectedFilter]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedFilters((prevSelectedFilters) => {
      let updatedFilters;
      if (prevSelectedFilters.includes(value)) {
        updatedFilters = prevSelectedFilters.filter((filter) => filter !== value);
        setSelectedFilter((prev) => ({
          ...prev,
          [value]: "",
        }));
        if (value === "action__contact__email__icontains") {
          setEmailValidationError(null);
        }
      } else {
        updatedFilters = [...prevSelectedFilters, value];
      }
      handleSubmit(
        updatedFilters.reduce((acc, filter) => {
          acc[filter] = selectedFilter[filter] || "";
          return acc;
        }, {})
      );
      return updatedFilters;
    });
  };

  const handleFilterValueChange = (value: string, filter: string) => {
    setSelectedFilter((prev: any) => ({
      ...prev,
      [filter]: value,
    }));
  };

  const handleDateToChange = (event: any) => {
    const value = event.target.value;
    setDateTo(value);
    setSelectedFilter((prev: any) => ({
      ...prev,
      created: value,
    }));
  };

  const resetFilters = () => {
    setSelectedFilters([]);
    const emptyFilters = filters.reduce((acc, filter) => {
      acc[filter.value] = "";
      return acc;
    }, {} as any);
    setSelectedFilter(emptyFilters);
    setDateTo("");
    setError(null);
    setEmailValidationError(null);
    sessionStorage.removeItem("selectedFilters");
    sessionStorage.removeItem("selectedFilter");
    handleSubmit({});
  };

  const apiPort = process.env.REACT_APP_API_PROTOCOL;
  const fetchingTenant = window.location.hostname.split(".")[0];

  const searchCustomerEmail = (skey: string) => {
    axios
      .get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/customers/contacts/search-by-email/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
          params: { email: skey },
        }
      )
      .then((res) => {
        if (res.status === 200) {
          setSuggestions(res.data);
          setError(null);
        }
      })
      .catch(() => {
        setError("Failed to fetch customer email suggestions.");
      });
  };

  const searchAssigneeSuggestions = (skey: string) => {
    axios
      .get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/users/users/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
          params: { username__icontains: skey },
        }
      )
      .then((res) => {
        if (res.status === 200) {
          setAssigneeSuggestions(res.data);
          setError(null);
        }
      })
      .catch(() => {
        setError("Failed to fetch assignee suggestions.");
      });
  };

  // Function to clean empty filters on close, then close modal
  const cleanEmptyFiltersAndClose = () => {
    // Remove filters with empty/null values
    const filteredSelectedFilters = selectedFilters.filter(
      (filter) => selectedFilter[filter] && selectedFilter[filter].toString().trim() !== ""
    );

    const filteredSelectedFilterValues = filteredSelectedFilters.reduce((acc, filter) => {
      acc[filter] = selectedFilter[filter];
      return acc;
    }, {} as any);

    setSelectedFilters(filteredSelectedFilters);
    setSelectedFilter(filteredSelectedFilterValues);

    sessionStorage.setItem("selectedFilters", JSON.stringify(filteredSelectedFilters));
    sessionStorage.setItem("selectedFilter", JSON.stringify(filteredSelectedFilterValues));

    closeFilterModal();
  };

  const handleApplyFilters = () => {
    let nextFilters = [...selectedFilters];
    let emailError = null;

    nextFilters = nextFilters.filter((filter) => {
      const value = selectedFilter[filter] || "";
      if (!value.trim()) {
        return false;
      }
      if (filter === "action__contact__email__icontains") {
        const matched = suggestions.find((s) => s.email === value);
        if (!matched) {
          emailError = "Please select a valid email from the list.";
          return false;
        }
      }
      return true;
    });

    setEmailValidationError(emailError);

    if (emailError) return; // Prevent close on email error

    const filterValuesToSubmit = nextFilters.reduce((acc, filter) => {
      acc[filter] = selectedFilter[filter];
      return acc;
    }, {} as any);

    setSelectedFilters(nextFilters);
    setSelectedFilter(filterValuesToSubmit);

    sessionStorage.setItem("selectedFilters", JSON.stringify(nextFilters));
    sessionStorage.setItem("selectedFilter", JSON.stringify(filterValuesToSubmit));

    handleSubmit(filterValuesToSubmit);
    closeFilterModal();
  };

  return (
    <Modal show={true} onHide={cleanEmptyFiltersAndClose} centered>
      <Modal.Header>
        <Modal.Title className="fw-bold">Filters</Modal.Title>
        <CloseButton onClick={cleanEmptyFiltersAndClose} />
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="px-3 py-2">
          {filters.map((filter) => (
            <Row key={filter.value} className="mb-3 align-items-center">
              <Col xs={12} md={4} className="text-nowrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedFilters.includes(filter.value)}
                      onChange={handleFilterChange}
                      value={filter.value}
                      color="primary"
                    />
                  }
                  label={filter.label}
                />
              </Col>
              <Col xs={12} md={8}>
                {selectedFilters.includes(filter.value) && (
                  <>
                    {filter.value === "created" ? (
                      <TextField
                        id="date-to"
                        label="Select Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: { style: { textTransform: "uppercase" } },
                        }}
                        fullWidth
                        value={dateTo}
                        onChange={handleDateToChange}
                      />
                    ) : filter.value === "action__contact__email__icontains" ? (
                      <Autocomplete
                        freeSolo
                        options={suggestions.map((option) => option.email)}
                        value={selectedFilter[filter.value] || ""}
                        onInputChange={(event, newInputValue) => {
                          searchCustomerEmail(newInputValue);
                          setSelectedFilter((prev: any) => ({
                            ...prev,
                            [filter.value]: newInputValue,
                          }));
                          if (emailValidationError) setEmailValidationError(null);
                        }}
                        onChange={(event, newValue) => {
                          const selectedSuggestion = suggestions.find(
                            (suggestion) => suggestion.email === newValue
                          );
                          setSelectedFilter((prev: any) => ({
                            ...prev,
                            [filter.value]: selectedSuggestion
                              ? selectedSuggestion.email
                              : newValue || "",
                          }));
                          setEmailValidationError(null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter.label}
                            variant="outlined"
                            error={!!emailValidationError}
                            helperText={emailValidationError || ""}
                          />
                        )}
                      />
                    ) : filter.value === "action__assignee__username__icontains" ? (
                      <Autocomplete
                        freeSolo
                        options={assigneeSuggestions.map(
                          (option) => option.username
                        )}
                        value={selectedFilter[filter.value] || ""}
                        onInputChange={(event, newInputValue) => {
                          searchAssigneeSuggestions(newInputValue);
                        }}
                        onChange={(event, newValue) => {
                          const selectedSuggestion = assigneeSuggestions.find(
                            (suggestion) => suggestion.username === newValue
                          );
                          setSelectedFilter((prev: any) => ({
                            ...prev,
                            action__assignee__username__icontains:
                              selectedSuggestion
                                ? selectedSuggestion.username
                                : "",
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter.label}
                            variant="outlined"
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        label={filter.label}
                        fullWidth
                        value={selectedFilter[filter.value] || ""}
                        onChange={(e) =>
                          handleFilterValueChange(e.target.value, filter.value)
                        }
                      />
                    )}
                  </>
                )}
              </Col>
            </Row>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={resetFilters}>
          Reset
        </Button>
        <Button variant="primary" onClick={handleApplyFilters}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InboxFilterModal;
